
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Users, Search, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/profiles';
import { useToast } from '@/hooks/use-toast';
import { usePinAuth } from '@/hooks/usePinAuth';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';
import CreateEmployeeDialog from '@/components/EmployeeManagement/CreateEmployeeDialog';
import EditEmployeeDialog from '@/components/EmployeeManagement/EditEmployeeDialog';
import PinManagement from '@/components/EmployeeManagement/PinManagement';

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pinUser } = usePinAuth();
  const [employees, setEmployees] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingEmployee, setEditingEmployee] = useState<Profile | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [pinManagementOpen, setPinManagementOpen] = useState(false);
  const [selectedEmployeeForPin, setSelectedEmployeeForPin] = useState<Profile | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(searchParams.get('create') === '1');
  const { toast } = useToast();

  useEffect(() => {
    if (pinUser) {
      fetchEmployees();
    }
  }, [pinUser]);

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setCreateDialogOpen(true);
    }
  }, [searchParams]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();

        // Managers / contremaîtres / admins: liste globale (RPC PIN mode)
        if (pinUser && ['manager', 'admin', 'contremaitre'].includes(pinUser.app_role)) {
          const { data, error } = await supabase.rpc('pin_list_profiles', { p_pin: pin });
          if (error) throw error;

          const normalized = ((data as any[]) || []).map((p: any) => ({
            id: p.id,
            first_name: p.first_name ?? null,
            last_name: p.last_name ?? null,
            email: p.email ?? null,
            phone: p.phone ?? null,
            position: p.position ?? null,
            hourly_rate: p.hourly_rate ?? null,
            hire_date: p.hire_date ?? null,
            is_active: typeof p.is_active === 'boolean' ? p.is_active : true,
            created_at: p.created_at ?? '',
            updated_at: p.updated_at ?? '',
          })) as Profile[];

          setEmployees(normalized);
        } else if (pinUser?.id) {
          // Employés: uniquement leur profil (RPC PIN mode)
          const { data, error } = await supabase.rpc('pin_get_profile', { p_pin: pin, p_profile_id: pinUser.id });
          if (error) throw error;

          const rows = (data as any[]) || [];
          const p = rows[0];
          const normalized: Profile[] = p
            ? [
                {
                  id: p.id,
                  first_name: p.first_name ?? null,
                  last_name: p.last_name ?? null,
                  email: p.email ?? null,
                  phone: p.phone ?? null,
                  position: p.position ?? null,
                  hourly_rate: p.hourly_rate ?? null,
                  hire_date: p.hire_date ?? null,
                  is_active: typeof p.is_active === 'boolean' ? p.is_active : true,
                  created_at: p.created_at ?? '',
                  updated_at: p.updated_at ?? '',
                },
              ]
            : [];

          setEmployees(normalized);
        } else {
          throw new Error('ID utilisateur non disponible');
        }
      } else {
        // Fallback JWT (compat)
        // Utiliser une fonction RPC qui contourne RLS pour les managers/admins
        if (pinUser && (pinUser.app_role === 'manager' || pinUser.app_role === 'admin')) {
          const { data, error } = await supabase.rpc('get_all_profiles_for_manager').select('*');

          if (error) {
            // Fallback vers la requête directe si la fonction RPC n'existe pas
            const { data: directData, error: directError } = await supabase.from('profiles').select('*').order('last_name', { ascending: true });

            if (directError) throw directError;
            setEmployees(directData || []);
          } else {
            setEmployees(data || []);
          }
        } else {
          // Pour les autres utilisateurs, essayer de récupérer leur propre profil
          if (pinUser?.id) {
            const { data, error } = await supabase.from('profiles').select('*').eq('id', pinUser.id).order('last_name', { ascending: true });

            if (error) throw error;
            setEmployees(data || []);
          } else {
            // Si pas d'ID utilisateur, afficher un message d'erreur
            throw new Error('ID utilisateur non disponible');
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des employés:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger la liste des employés",
        variant: "destructive",
      });
      setEmployees([]); // Initialiser avec un tableau vide en cas d'erreur
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployeeStatus = async (employeeId: string, currentStatus: boolean) => {
    try {
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_update_profile', {
          p_pin: pin,
          p_profile_id: employeeId,
          p_is_active: !currentStatus,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('profiles').update({ is_active: !currentStatus }).eq('id', employeeId);
        if (error) throw error;
      }

      setEmployees(employees.map(emp => 
        emp.id === employeeId 
          ? { ...emp, is_active: !currentStatus }
          : emp
      ));

      toast({
        title: "Succès",
        description: `Employé ${!currentStatus ? 'activé' : 'désactivé'} avec succès`,
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut de l'employé",
        variant: "destructive",
      });
    }
  };

  const deleteEmployee = async (employeeId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet employé ? Cette action est irréversible.')) {
      return;
    }

    try {
      if (shouldUsePinMode()) {
        // Soft-delete en mode PIN
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_update_profile', {
          p_pin: pin,
          p_profile_id: employeeId,
          p_is_active: false,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('profiles').delete().eq('id', employeeId);
        if (error) throw error;
      }

      setEmployees(employees.filter(emp => emp.id !== employeeId));
      toast({
        title: "Succès",
        description: "Employé supprimé avec succès",
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'employé",
        variant: "destructive",
      });
    }
  };

  const handleEditEmployee = (employee: Profile) => {
    setEditingEmployee(employee);
    setEditDialogOpen(true);
  };

  const handlePinManagement = (employee: Profile) => {
    setSelectedEmployeeForPin(employee);
    setPinManagementOpen(true);
  };

  const filteredEmployees = employees.filter(employee => {
    const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.toLowerCase();
    const email = employee.email?.toLowerCase() || '';
    const position = employee.position?.toLowerCase() || '';
    
    return fullName.includes(searchTerm.toLowerCase()) ||
           email.includes(searchTerm.toLowerCase()) ||
           position.includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="text-center">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/manager')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">Gestion des Employés</h1>
          </div>
        </div>

        {/* Search and Actions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-4 items-center">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, email ou poste..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <CreateEmployeeDialog
                onEmployeeCreated={fetchEmployees}
                open={createDialogOpen}
                onOpenChange={(open) => {
                  setCreateDialogOpen(open);
                  if (!open && searchParams.get('create') === '1') {
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.delete('create');
                    setSearchParams(nextParams, { replace: true });
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{employees.length}</div>
              <div className="text-sm text-muted-foreground">Total employés</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {employees.filter(emp => emp.is_active).length}
              </div>
              <div className="text-sm text-muted-foreground">Actifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-600">
                {employees.filter(emp => !emp.is_active).length}
              </div>
              <div className="text-sm text-muted-foreground">Inactifs</div>
            </CardContent>
          </Card>
        </div>

        {/* Employee List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Liste des employés ({filteredEmployees.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredEmployees.map((employee) => (
                <div
                  key={employee.id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-medium">
                          {employee.first_name} {employee.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {employee.email}
                        </div>
                        {employee.position && (
                          <div className="text-sm text-muted-foreground">
                            {employee.position}
                          </div>
                        )}
                        {employee.hire_date && (
                          <div className="text-xs text-muted-foreground">
                            Embauché le: {new Date(employee.hire_date).toLocaleDateString('fr-FR')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={employee.is_active ? "default" : "secondary"}>
                      {employee.is_active ? "Actif" : "Inactif"}
                    </Badge>
                    {employee.hourly_rate && (
                      <Badge variant="outline">
                        {employee.hourly_rate}$/h
                      </Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleEmployeeStatus(employee.id, employee.is_active)}
                    >
                      {employee.is_active ? "Désactiver" : "Activer"}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handlePinManagement(employee)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Gérer PIN
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEditEmployee(employee)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => deleteEmployee(employee.id)}
                      className="text-red-600 hover:text-blue-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {filteredEmployees.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun employé trouvé
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <EditEmployeeDialog
          employee={editingEmployee}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onEmployeeUpdated={fetchEmployees}
        />

        {/* Dialog de gestion des PIN */}
        {selectedEmployeeForPin && (
          <Dialog open={pinManagementOpen} onOpenChange={setPinManagementOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              {/* Accessibilité: DialogContent doit avoir un DialogTitle */}
              <DialogHeader className="sr-only">
                <DialogTitle>Gestion du PIN</DialogTitle>
              </DialogHeader>
              <PinManagement
                profileId={selectedEmployeeForPin.id}
                employeeName={`${selectedEmployeeForPin.first_name} ${selectedEmployeeForPin.last_name}`}
                onPinUpdated={(newPin) => {
                  toast({
                    title: "PIN mis à jour",
                    description: `Le PIN de ${selectedEmployeeForPin.first_name} a été mis à jour`,
                  });
                }}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default EmployeeManagement;
